import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AddAdminFormGroup, AdminDialogType } from './add-amin-dialog.vm';
import { passwordOrEmptyRegex, passwordRegex } from 'src/app/core/validation/password-validation';
import { DATE_SLASH_FORMAT } from 'src/app/core/formatting/date-formatting';
import * as moment from 'moment';
import { normalizeEmail } from 'src/app/core/validation/email-validation';
import { catchError, Observable, of } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { AddBlacklistItemDialogComponent } from '../add-blacklist-item-dialog/add-blacklist-item-dialog.component';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { AdminService } from 'src/app/services/admin.service';
import { mapAdminPermissionsApiModelToVm, mapAdminPermissionsVmToApiModel } from './add-admin-dialog.mappings';
import { Admin, CreateAdminRequest } from '@mol-limo-reg/admin-api';
import { AuthService } from 'src/app/core/services/auth.service';

@Injectable()
export class AddAdminDialogService {
  constructor(
    private dialogRef: MatDialogRef<AddBlacklistItemDialogComponent>,
    private snackbarService: SnackbarService,
    private adminService: AdminService,
    private readonly authService: AuthService
  ) {}

  private addAdminFormGroup: FormGroup<AddAdminFormGroup> | undefined;

  public getAddAdminFormGroup(dialogType: AdminDialogType, formData?: Admin): FormGroup<AddAdminFormGroup> {
    if (!this.addAdminFormGroup) {
      const isPasswordRequired = dialogType === AdminDialogType.Add;
      this.addAdminFormGroup = this.createAddAdminFormGroup(isPasswordRequired);
    }
    if (formData) {
      this.addAdminFormGroup.patchValue({
        email: formData.email,
        name: formData.name,
        permissions: formData.permissions.map((permission) => mapAdminPermissionsApiModelToVm[permission]),
      });
    }
    return this.addAdminFormGroup;
  }

  public submitForm() {
    const apiModel = this.mapFormDataToApiModel();
    const { name, email } = apiModel;
    this.adminService
      .createAdmin$(apiModel)
      .pipe(catchError(() => this.errorState(name, email)))
      .subscribe(() => this.successState(name, email));
  }

  public updateForm(previousData: Admin) {
    const oldName = previousData.name;
    const email = normalizeEmail(previousData.email);
    const newName = this.addAdminFormGroup?.get('name')?.value;
    const newPassword = this.addAdminFormGroup?.get('password')?.value;
    const permissions = this.addAdminFormGroup?.value.permissions?.map((permission) => mapAdminPermissionsVmToApiModel[permission]) || [];
    this.adminService
      .updateAdmin$(previousData.id, {
        name: newName ?? '',
        permissions,
        ...(newPassword && { password: newPassword }),
      })
      .pipe(
        catchError(() => {
          this.snackbarService.error(`”${oldName} (${email})” adatainak módosítása során hiba lépett fel!`);
          this.dialogRef.close({ success: false });
          return of();
        })
      )
      .subscribe(() => {
        this.snackbarService.success(`”${oldName} (${email})” adatainak módosítása sikeres!`);
        this.dialogRef.close({ success: true });
        this.refreshProfileIfOwn(previousData);
      });
  }

  private createAddAdminFormGroup(isPasswordRequired: boolean): FormGroup<AddAdminFormGroup> {
    return new FormGroup<AddAdminFormGroup>({
      email: new FormControl('', {
        validators: [Validators.email, Validators.required],
        nonNullable: true,
      }),
      name: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      password: new FormControl('', {
        validators: isPasswordRequired ? [Validators.required, Validators.pattern(passwordRegex)] : [Validators.pattern(passwordOrEmptyRegex)],
        nonNullable: true,
      }),
      createdAt: new FormControl(
        { value: moment().format(DATE_SLASH_FORMAT), disabled: true },
        {
          nonNullable: true,
        }
      ),
      permissions: new FormControl(undefined, {
        validators: [Validators.required],
        nonNullable: true,
      }),
    });
  }

  private mapFormDataToApiModel(): CreateAdminRequest {
    const email = normalizeEmail(this.addAdminFormGroup?.value.email!);
    const name = this.addAdminFormGroup?.value.name ?? '';
    const password = this.addAdminFormGroup?.value.password ?? '';
    const permissions = this.addAdminFormGroup?.value.permissions?.map((permission) => mapAdminPermissionsVmToApiModel[permission]) || [];
    return {
      email,
      name,
      password,
      permissions,
    };
  }

  private errorState(name: string, email: string): Observable<void> {
    this.snackbarService.error(`”${name} (${email})” adatainak módosítása során hiba lépett fel!`);
    this.dialogRef.close({ success: false });
    return of();
  }

  private successState(name: string, email: string): void {
    this.snackbarService.success(`”${name} (${email})” sikeresen hozzáadva az adminokhoz!`);
    this.dialogRef.close({ success: true });
  }

  private refreshProfileIfOwn(adminData: Admin) {
    const ownProfile = this.authService.profile()?.id === adminData.id;
    if (ownProfile) {
      this.authService.refreshProfile();
    }
  }
}
